import type { ComponentType, SVGProps } from 'react'
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  CalendarIcon,
  ChartBarIcon,
  HomeIcon,
  TicketIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid'

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>

/**
 * Where a destination is offered:
 *  - `header`   the horizontal bar on public/marketing pages (HomepageNav)
 *  - `teamMenu` the "LodgeLink" dropdown on those same pages, which is the full
 *               app menu for signed-in members browsing outside the dashboard
 *  - `sidebar`  the dashboard sidebar (DashboardLayout)
 */
export type NavSurface = 'header' | 'teamMenu' | 'sidebar'

/**
 * `lodge_leadership` covers board members, lodge admins and root admins - the
 * same test both navs used to spell out inline.
 */
export type NavRole = 'lodge_leadership' | 'root_admin'

export interface NavRoles {
  isBoardMember: boolean
  isLodgeAdmin: boolean
  isRootAdmin: boolean
}

export interface NavItem {
  label: string
  url: string
  icon?: NavIcon
}

interface NavEntry {
  label: string
  /** Returns null when the destination does not exist for this member. */
  url: (lodgeId?: number | null) => string | null
  icon?: NavIcon
  surfaces: NavSurface[]
  requiredRole?: NavRole
  /**
   * The destination is served by a lodge-scoped controller, which
   * ApplicationController bounces to the lodge picker whenever there is no
   * lodge in context. Offering it to a root admin who hasn't chosen a lodge
   * yet is offering a link that can only fail, so it's hidden until they do.
   * Ordinary members always have their own lodge, so this never hides
   * anything from them. Mirrors SKIP_ACTIVE_MEMBERSHIP_REDIRECT: an entry
   * needs this flag exactly when its controller is *not* on that list.
   */
  requiresLodge?: boolean
}

/**
 * The single inventory of authenticated destinations. Both navs read from this,
 * so a link can no longer exist in one and be missing from the other, and a
 * destination cannot end up with two different labels.
 */
const NAV_ENTRIES: NavEntry[] = [
  { label: 'Home', url: () => '/', surfaces: ['header'] },
  { label: 'Dashboard', url: () => '/dashboard', icon: HomeIcon, surfaces: ['header', 'sidebar'] },
  {
    label: 'Vouchers',
    url: () => '/vouchers',
    icon: TicketIcon,
    surfaces: ['header', 'teamMenu', 'sidebar'],
    requiresLodge: true,
  },
  {
    label: 'Committees',
    url: () => '/committees',
    icon: UserGroupIcon,
    surfaces: ['header', 'teamMenu', 'sidebar'],
    requiresLodge: true,
  },
  {
    label: 'Events',
    url: () => '/events',
    icon: CalendarIcon,
    surfaces: ['teamMenu', 'sidebar'],
    requiresLodge: true,
  },
  {
    label: 'Members',
    url: () => '/members',
    icon: UserGroupIcon,
    surfaces: ['header', 'teamMenu', 'sidebar'],
    requiredRole: 'lodge_leadership',
    requiresLodge: true,
  },
  {
    label: 'Pipeline',
    url: () => '/pipeline',
    icon: ChartBarIcon,
    surfaces: ['sidebar'],
    requiredRole: 'lodge_leadership',
    requiresLodge: true,
  },
  {
    label: 'Payments',
    url: () => '/payments',
    icon: BanknotesIcon,
    surfaces: ['teamMenu', 'sidebar'],
    requiredRole: 'lodge_leadership',
    requiresLodge: true,
  },
  {
    label: 'Accounts',
    url: () => '/accounts',
    icon: BanknotesIcon,
    surfaces: ['teamMenu', 'sidebar'],
    requiredRole: 'lodge_leadership',
    requiresLodge: true,
  },
  {
    label: 'Lodges',
    // Deliberately not on the sidebar: the root-admin acting-lodge switcher
    // there already links to /lodges, and both are root-admin only, so
    // listing it here too would put the same destination in one sidebar
    // twice. The other two surfaces have no switcher.
    url: () => '/lodges',
    icon: BuildingLibraryIcon,
    surfaces: ['header', 'teamMenu'],
    requiredRole: 'root_admin',
  },
  {
    label: 'Your Lodge',
    url: (lodgeId) => (lodgeId != null ? `/lodges/${lodgeId}` : null),
    icon: BuildingLibraryIcon,
    surfaces: ['teamMenu', 'sidebar'],
  },
]

export const publicNavItems: NavItem[] = [{ label: 'Home', url: '/' }]

function hasRequiredRole(required: NavRole | undefined, roles: NavRoles): boolean {
  if (!required) return true
  if (required === 'root_admin') return roles.isRootAdmin
  return roles.isBoardMember || roles.isLodgeAdmin || roles.isRootAdmin
}

export function getNavItems(
  surface: NavSurface,
  roles: NavRoles,
  lodgeId?: number | null,
): NavItem[] {
  return NAV_ENTRIES.flatMap((entry) => {
    if (!entry.surfaces.includes(surface)) return []
    if (!hasRequiredRole(entry.requiredRole, roles)) return []
    if (entry.requiresLodge && lodgeId == null) return []

    const url = entry.url(lodgeId)
    if (url == null) return []

    return [{ label: entry.label, url, icon: entry.icon }]
  })
}

/** Matches `base` and anything nested under it, but not a sibling like `/lodges/123`. */
function pathMatches(currentPath: string, base: string): boolean {
  if (base === '/') return currentPath === '/'
  return currentPath === base || currentPath.startsWith(`${base}/`)
}

export function isNavItemCurrent(
  url: string,
  currentPath: string,
  lodgeId?: number | null,
): boolean {
  // "Lodges" is the index of every other lodge; the member's own lodge has its
  // own entry, so browsing that must not light both up.
  if (url === '/lodges' && lodgeId != null) {
    return pathMatches(currentPath, '/lodges') && !pathMatches(currentPath, `/lodges/${lodgeId}`)
  }

  return pathMatches(currentPath, url)
}
